import { useState, FormEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import AuthCard from './AuthCard';
import { resendSignupConfirmation } from '../../../core/auth/supabase-auth';

interface LoginFormProps {
  onLogin: (email: string, password: string) => Promise<void>;
  error: string | null;
}


const LoginForm = ({ onLogin, error: authError }: LoginFormProps) => {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleResend = async () => {
    setResendStatus('sending');
    const ok = await resendSignupConfirmation(email);
    setResendStatus(ok ? 'sent' : 'error');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setResendStatus('idle');

    if (!email || !password) {
      setFormError(t('login.errors.missingFields'));
      return;
    }

    try {
      setLoading(true);
      setFormError('');
      await onLogin(email, password);
    } catch {
      // Error is now set on AuthContext by AuthContext.login.
      // It's read via the authError prop. No-op here.
    } finally {
      setLoading(false);
    }
  };

  const displayError = formError || authError;

  return (
    <AuthCard
      title={t('login.title')}
      titleAccent={t('login.titleAccent')}
      subtitle={t('login.subtitle')}
      footer={
        <span className="inline-flex flex-wrap items-center justify-center gap-1.5">
          <span>{t('login.noAccount')}</span>
          <Link
            to="/register"
            className="font-medium text-brand-navy underline decoration-brand-teal decoration-2 underline-offset-4 hover:text-brand-teal transition-colors"
          >
            {t('login.registerLink')}
          </Link>
        </span>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit} noValidate>
        {displayError && (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {displayError}
            {displayError === t('login.errors.emailNotConfirmed') && (
              <div className="mt-2">
                <button
                  type="button"
                  disabled={resendStatus === 'sending' || resendStatus === 'sent'}
                  onClick={handleResend}
                  className={`underline font-medium disabled:opacity-60 ${
                    resendStatus === 'error'
                      ? 'text-red-900 font-semibold hover:text-red-700'
                      : 'hover:text-red-900'
                  }`}
                >
                  {resendStatus === 'sent'
                    ? t('login.errors.emailNotConfirmedResendSuccess')
                    : resendStatus === 'error'
                    ? t('login.errors.emailNotConfirmedResendError')
                    : t('login.errors.emailNotConfirmedResend')}
                </button>
              </div>
            )}
          </div>
        )}

        <div>
          <label
            htmlFor="email"
            className="block text-[13px] font-medium text-brand-ink"
          >
            {t('login.emailLabel')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            placeholder={t('login.emailPlaceholder')}
            className="mt-1.5 block w-full rounded-lg border border-brand-rule/30 bg-card px-3.5 py-2.5 text-[15px] text-brand-ink placeholder:text-brand-fog/70 transition focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/25 disabled:opacity-60"
          />
        </div>

        <div>
          <div className="flex items-baseline justify-between">
            <label
              htmlFor="password"
              className="block text-[13px] font-medium text-brand-ink"
            >
              {t('login.passwordLabel')}
            </label>
            <Link
              to="/forgot-password"
              className="text-xs font-medium text-brand-fog hover:text-brand-teal transition-colors"
            >
              {t('login.forgotPassword')}
            </Link>
          </div>
          <div className="relative mt-1.5">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              placeholder={t('login.passwordPlaceholder')}
              className="block w-full rounded-lg border border-brand-rule/30 bg-card px-3.5 py-2.5 pr-11 text-[15px] text-brand-ink placeholder:text-brand-fog/70 transition focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/25 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={
                showPassword
                  ? t('login.togglePasswordHide')
                  : t('login.togglePasswordShow')
              }
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-brand-fog hover:text-brand-navy focus-visible:outline-none focus-visible:text-brand-teal"
            >
              {showPassword ? <EyeOff className="h-4.5 w-4.5" size={18} /> : <Eye className="h-4.5 w-4.5" size={18} />}
            </button>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="group relative inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-brand-teal px-6 text-[14px] font-semibold tracking-tight text-brand-navy transition-all hover:bg-brand-tealLight hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-brand-teal"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('login.submittingButton')}
            </>
          ) : (
            <>
              {t('login.submitButton')}
              <span aria-hidden className="transition-transform group-hover:translate-x-0.5">
                →
              </span>
            </>
          )}
        </button>
      </form>
    </AuthCard>
  );
};

export default LoginForm;
